// Typed client for /agent/articles* and /news*.
//
// Mirrors backend/internal/articles/store.go. The agent-side surface
// renders its UI off a stable mock-derived `ArticleRow` shape; this
// module exposes both the wire types (raw network shape) and an
// adapter that converts a wire row into `ArticleRow` so existing
// pages keep their types unchanged.
//
// Network reads are gated behind `import.meta.env.VITE_ARTICLES_API`
// — when unset, the existing mocks in pages/pro/agent/articles/data.ts
// keep flowing. Flip the flag once the backend is deployed.

import { apiDelete, apiFetch, apiJson, apiPatchJson, apiPostJson } from "./api";
import type { ArtKind, ArtStatus, ArticleRow } from "@/pages/pro/agent/articles/data";

// ─── wire types ──────────────────────────────────────────────────────────────

export type ArticleState =
  | "draft"
  | "awaiting_review"
  | "in_review"
  | "changes_requested"
  | "published"
  | "scheduled"
  | "rejected"
  | "archived";

export interface Section {
  kind: "p" | "h2" | "pull" | "callout" | "sources" | "image";
  text?: string;
  items?: string[];
}

export interface ArticleStats {
  views: number;
  reads: number;
  leads: number;
  comments: number;
}

// ArticleAuthor is the public-safe author snippet the /news endpoints
// join onto each article — no email (the backend redacts it). Drives
// the byline and the /author/{slug} link.
export interface ArticleAuthor {
  id: string;
  name: string;
  kind: string; // "human" | "house"
  slug?: string;
  editorial_role?: string;
  role?: string;
}

export interface ArticleWire {
  id: string;
  author_id: string;
  author?: ArticleAuthor;
  kind: ArtKind;
  title: string;
  dek: string;
  hero_image: string;
  slug?: string | null;
  seo_meta: Record<string, unknown>;
  audience: "public" | "pro" | "members";
  lead_capture: boolean;
  tags: string[];
  body: Section[];
  word_count: number;
  read_time_secs: number;
  state: ArticleState;
  reviewer_id?: string | null;
  flags: number;
  version: number;
  submitted_at?: string | null;
  claimed_at?: string | null;
  scheduled_for?: string | null;
  published_at?: string | null;
  archived_at?: string | null;
  created_at: string;
  updated_at: string;
  stats: ArticleStats;
}

export interface RevisionWire {
  id: string;
  article_id: string;
  version: number;
  title: string;
  dek: string;
  body: Section[];
  saved_by: string;
  saved_at: string;
}

export interface ReviewEventWire {
  id: string;
  article_id: string;
  kind: string;
  actor_kind: "author" | "admin" | "system";
  actor_id: string;
  body: string;
  anchor: string;
  created_at: string;
}

export interface ArticleDetailResp {
  article: ArticleWire;
  revisions: RevisionWire[];
  events: ReviewEventWire[];
}

export interface ArticleListResp {
  articles: ArticleWire[];
  total: number;
}

export interface NewsFeedResp {
  lead: ArticleWire | null;
  briefs: ArticleWire[];
}

// ─── feature flag ────────────────────────────────────────────────────────────

// Set VITE_ARTICLES_API=1 to switch the surface from mocks to live API.
// Reading via import.meta keeps it tree-shake-safe in tests and prod
// builds — when the flag is unset the network calls are simply not
// invoked by the page-level swap.
export function articlesApiEnabled(): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const env = (import.meta as any).env ?? {};
  return env.VITE_ARTICLES_API === "1" || env.VITE_ARTICLES_API === "true";
}

// ─── agent fetch wrappers ────────────────────────────────────────────────────

export interface AgentListFilter {
  state?: ArticleState | "";
  kind?: ArtKind | "";
  q?: string;
  limit?: number;
  offset?: number;
}

function buildQS(params: Record<string, unknown>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.append(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

export async function listAgentArticles(filter: AgentListFilter = {}): Promise<ArticleListResp> {
  return apiJson<ArticleListResp>(`/agent/articles${buildQS({ ...filter })}`);
}

// ─── insights (PROP-17) ──────────────────────────────────────────────────────
//
// The agent Reports "INSIGHTS" section surfaces each of the agent's
// published, public-audience articles as a lead-gen insight with its
// live reader + lead counts. The read/lead-tracking pipeline the
// original ticket said was missing now exists: article_stats.reads /
// .leads are maintained by the articles epic's counter flush, exposed
// on every ArticleWire. So this is a pure selection over the existing
// /agent/articles feed — no fabricated numbers.

export interface InsightRow {
  id: string;
  title: string;
  kind: ArtKind;
  readers: number;
  leads: number;
}

// toInsights selects the public lead-gen insights from an agent's
// article list and maps each to its reader/lead counts, sorted by
// readership desc (the most-read pieces lead the section). Only
// public-audience articles count — pro/members-only pieces aren't
// public insights. Pure + total over the input so it's unit-testable.
export function toInsights(rows: ArticleWire[]): InsightRow[] {
  return rows
    .filter((w) => w.audience === "public")
    .map((w): InsightRow => ({
      id: w.id,
      title: w.title,
      kind: w.kind,
      readers: Math.max(0, Math.floor(Number(w.stats?.reads ?? 0)) || 0),
      leads: Math.max(0, Math.floor(Number(w.stats?.leads ?? 0)) || 0),
    }))
    .sort((a, b) => b.readers - a.readers);
}

export async function createAgentArticle(body: {
  kind: ArtKind;
  title?: string;
  dek?: string;
}): Promise<ArticleWire> {
  return apiPostJson<ArticleWire>(`/agent/articles`, body);
}

export async function getAgentArticle(id: string): Promise<ArticleDetailResp> {
  return apiJson<ArticleDetailResp>(`/agent/articles/${encodeURIComponent(id)}`);
}

export async function patchAgentArticle(
  id: string,
  patch: {
    title?: string;
    dek?: string;
    hero_image?: string;
    kind?: ArtKind;
    body?: Section[];
    tags?: string[];
    audience?: "public" | "pro" | "members";
    lead_capture?: boolean;
    slug?: string;
    seo_meta?: Record<string, unknown>;
  },
): Promise<ArticleWire> {
  return apiPatchJson<ArticleWire>(`/agent/articles/${encodeURIComponent(id)}`, patch);
}

// uploadAgentArticleImage POSTs a multipart image to the agent image
// endpoint and returns the stored object-storage URL — used by the
// composer's cover-image button (PROP-53). apiPostJson only speaks
// JSON, so this uses apiFetch with FormData; credentials still ride.
export async function uploadAgentArticleImage(id: string, file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  const res = await apiFetch(`/agent/articles/${encodeURIComponent(id)}/image`, {
    method: "POST",
    body: form,
  });
  const data = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
  if (!res.ok || !data.url) {
    throw new Error(data.error || `image upload failed (HTTP ${res.status})`);
  }
  return data.url;
}

export async function submitAgentArticle(id: string): Promise<ArticleWire> {
  return apiPostJson<ArticleWire>(`/agent/articles/${encodeURIComponent(id)}/submit`, {});
}

export async function withdrawAgentArticle(id: string): Promise<ArticleWire> {
  return apiPostJson<ArticleWire>(`/agent/articles/${encodeURIComponent(id)}/withdraw`, {});
}

export async function archiveAgentArticle(id: string): Promise<void> {
  await apiDelete(`/agent/articles/${encodeURIComponent(id)}`);
}

// ─── buyer Newsroom wrappers ─────────────────────────────────────────────────

export async function listNews(kind?: ArtKind | "", limit = 24): Promise<NewsFeedResp> {
  return apiJson<NewsFeedResp>(`/news${buildQS({ kind, limit })}`);
}

export async function getNewsBySlug(slug: string): Promise<ArticleWire> {
  return apiJson<ArticleWire>(`/news/${encodeURIComponent(slug)}`);
}

export async function reportNewsEvent(
  slug: string,
  kind: "view" | "read" | "lead",
): Promise<void> {
  // 204 — apiFetch already swallows the empty body; we just need to
  // ignore the response.
  await apiFetch(`/news/${encodeURIComponent(slug)}/event`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ kind }),
  });
}

// ─── tag catalogue (agent composer typeahead) ───────────────────────────────

export interface TagOption {
  tag: string;            // canonical key
  label: string;          // display form
  kind: "URA" | "Policy" | "Market" | "Brief" | "Guide";
  count: number;
  leads: number;
}

export async function listAgentTags(): Promise<TagOption[]> {
  const resp = await apiJson<{ tags: TagOption[] }>(`/agent/articles/tags`);
  return resp.tags ?? [];
}

// ─── public author page (/author/{slug}) ───────────────────────────────────

export interface PublicAuthor {
  id: string;
  name: string;
  slug: string;
  kind: string;
  bio: string;
  photo_url: string;
}

export interface PublicAuthorResp {
  author: PublicAuthor;
  articles: ArticleWire[];
}

export async function getPublicAuthor(slug: string): Promise<PublicAuthorResp> {
  return apiJson<PublicAuthorResp>(`/news/author/${encodeURIComponent(slug)}`);
}

// ─── buyer-side card shape ──────────────────────────────────────────────────
//
// The buyer FE (Explore.tsx) renders cards off a minimal Article shape:
// no id, no slug, just kind/title/dek/source/date/readTime. The
// adapter below builds that from the wire row so the existing
// NewsroomView / ArticleView render unchanged.

export interface BuyerNewsCard {
  id: string;
  slug?: string;
  kind: "URA" | "Policy" | "Market" | "Brief";
  title: string;
  dek?: string;
  heroImage?: string;
  source: string;
  authorSlug?: string;
  date: string;
  readTime: string;
  // Visible-comment count from article_stats.comments, already kept in
  // sync by the comments store (PROP-78). Surfaced on the Newsroom card
  // as a tiny "💬 N" badge (PROP-81) so readers can spot which articles
  // have discussion before opening them; hidden when zero.
  comments: number;
}

export function adaptBuyerCard(w: ArticleWire): BuyerNewsCard {
  // Buyer cards never show "Guide" — it's an internal-only kind in the
  // current consumer UI. Coerce to "Brief" if a Guide ever leaks
  // through; FE has a fallback chip for unknown kinds.
  const kind: BuyerNewsCard["kind"] =
    w.kind === "URA" || w.kind === "Policy" || w.kind === "Market" || w.kind === "Brief"
      ? w.kind
      : "Brief";
  // stats can be absent on legacy / offline-mock payloads, so probe
  // defensively. Negative or non-finite values are clamped to 0 to
  // keep the badge stable even if the backend ever emits garbage.
  const rawComments = Number(w.stats?.comments ?? 0);
  const comments = Number.isFinite(rawComments) && rawComments > 0 ? Math.floor(rawComments) : 0;
  return {
    id: w.id,
    slug: w.slug ?? undefined,
    kind,
    title: w.title,
    dek: w.dek || undefined,
    heroImage: w.hero_image || undefined,
    // Byline = the joined author name ("ProProp Data Lab" for house
    // articles, the agent's name otherwise). Falls back to "ProProp
    // Editorial" only if the wire carries no author (offline mock).
    source: w.author?.name || "ProProp Editorial",
    authorSlug: w.author?.slug,
    date: w.published_at ? formatDate(w.published_at) : formatDate(w.updated_at),
    readTime: w.read_time_secs > 0 ? `${Math.max(1, Math.round(w.read_time_secs / 60))} min` : "3 min",
    comments,
  };
}

// ─── adapter: wire → ArticleRow ──────────────────────────────────────────────
//
// The FE page-level type (`ArticleRow` in pages/pro/agent/articles/data.ts)
// is the original mock shape. Keeping the page on that type means the
// switch from mock → live is a one-line swap in the page hook, not a
// rewrite. The cost is this small mapping function — same pattern as
// `frontend/src/lib/agentCases.ts:adaptCase`.

const STATE_TO_STATUS: Record<ArticleState, ArtStatus | null> = {
  draft: "Draft",
  awaiting_review: "Review",
  in_review: "Review",
  changes_requested: "Draft",
  published: "Published",
  scheduled: "Scheduled",
  rejected: "Archived",
  archived: "Archived",
};

export function adaptArticleRow(w: ArticleWire): ArticleRow {
  const status = STATE_TO_STATUS[w.state] ?? "Draft";
  return {
    id: w.id,
    title: w.title,
    dek: w.dek,
    kind: w.kind,
    status,
    updated: relativeTime(w.updated_at),
    published: w.published_at ? formatDate(w.published_at) : undefined,
    views: w.stats.views,
    reads: w.stats.reads,
    leads: w.stats.leads,
    comments: w.stats.comments,
    readTime: readTimeLabel(w.read_time_secs),
    tags: w.tags ?? [],
  };
}

function readTimeLabel(secs: number): string {
  if (!secs || secs <= 0) return "1 min";
  const mins = Math.max(1, Math.round(secs / 60));
  return `${mins} min`;
}

// formatDate produces the "May 9, 2026" string used by ArticleRow.published.
// Locale-fixed to en-SG to keep the agent surface consistent with the
// rest of the workspace.
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-SG", { year: "numeric", month: "short", day: "numeric" });
}

// relativeTime mirrors the "2 hours ago" string the mock data uses.
// Falls back to formatDate for anything older than a week.
function relativeTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const secs = (Date.now() - d.getTime()) / 1000;
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)} minutes ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} hours ago`;
  if (secs < 86400 * 7) return `${Math.floor(secs / 86400)} days ago`;
  return formatDate(iso);
}
